/* eslint-disable prefer-destructuring */
import { AwsRum } from 'aws-rum-web'; // not sure why the disable is required but eslint is not picking up aws rum
import LocalStorageClient from './LocalStorageClient';

const RUM_TRACKING_SERVICE_NAME = 'AWS-RUM';
const NO_RUM_TRACKING_SERVICE_NAME = 'auto';
const DEVICE_CODE_KEY = 'device-code';
const DEVICE_CODE_BY_KEY = 'device-code-by';
export const TRACKING_FLUSH_METHODS = {
  NONE   : 'none',
  FETCH  : 'dispatch',
  BEACON : 'dispatchBeacon',
};

const APP_MONITOR_ID = process.env.APP_MONITOR_ID;
const REACT_APP_REGION = process.env.REACT_APP_REGION;
const APP_MONITOR_SAMPLE_RATE = process.env.APP_MONITOR_SAMPLE_RATE;
const APP_MONITOR_GUEST_ROLE_ARN = process.env.APP_MONITOR_GUEST_ROLE_ARN;
const APP_MONITOR_IDENTITY_POOL_ID = process.env.APP_MONITOR_IDENTITY_POOL_ID;

const TrackingService = {
  init: () => {
    try {
      if (!APP_MONITOR_ID || !REACT_APP_REGION || !APP_MONITOR_SAMPLE_RATE || !APP_MONITOR_GUEST_ROLE_ARN || !APP_MONITOR_IDENTITY_POOL_ID) {
        return;
      }

      const APPLICATION_ID = APP_MONITOR_ID;
      const APPLICATION_VERSION = '1.0.0';
      const APPLICATION_REGION = REACT_APP_REGION;

      const config = {
        sessionSampleRate: APP_MONITOR_SAMPLE_RATE,
        guestRoleArn: APP_MONITOR_GUEST_ROLE_ARN,
        identityPoolId: APP_MONITOR_IDENTITY_POOL_ID,
        endpoint: `https://dataplane.rum.${APPLICATION_REGION}.amazonaws.com`,
        telemetries: ['performance', 'http', 'errors'],
        allowCookies: false,
        enableXRay: false,
      };

      window.awsRum = new AwsRum(
        APPLICATION_ID,
        APPLICATION_VERSION,
        APPLICATION_REGION,
        config,
      );

      if (window.awsRum) {
        console.log('AWS RUM INITIATED');
        const existingDeviceCode = LocalStorageClient.get(DEVICE_CODE_KEY);
        if (!existingDeviceCode) {
          // New user: store the SDK-generated session UUID as device code
          const rumGeneratedId = window.awsRum.eventCache?.sessionManager?.userId;
          if (rumGeneratedId) {
            LocalStorageClient.set(DEVICE_CODE_KEY, rumGeneratedId);
            LocalStorageClient.set(DEVICE_CODE_BY_KEY, RUM_TRACKING_SERVICE_NAME);
          }
        }
        // Monkey-patch getUserId so user_details.userId in ALL RUM events reflects
        // the stable device code instead of a placeholder UUID.
        // NOTE: This uses an internal SDK method — aws-rum-web is pinned in package.json;
        // verify after upgrades (currently 1.25.0).
        if (window.awsRum.eventCache?.sessionManager) {
          const sessionManager = window.awsRum.eventCache.sessionManager;
          sessionManager.getUserId = () => {
            try {
              return LocalStorageClient.get(DEVICE_CODE_KEY) || sessionManager.userId;
            } catch (e) {
              return sessionManager.userId;
            }
          };
        }
        TrackingService.initializeGlobalClickTracking();
      }
    } catch (error) {
      console.log('AWS RUM FAILED', error);
      // Ignore errors thrown during CloudWatch RUM web client initialization
    }
  },
  getServiceName: () => {
    if (window.awsRum) {
      return RUM_TRACKING_SERVICE_NAME;
    }
    return NO_RUM_TRACKING_SERVICE_NAME;
  },
  getDeviceCode: () => {
    try {
      return LocalStorageClient.get(DEVICE_CODE_KEY);
    } catch (e) {
      //
    }
    return null;
  },
  setDeviceCode: (id, type) => {
    // Only allow set device code if one wasnt already generated
    if (LocalStorageClient.get(DEVICE_CODE_KEY)) {
      return;
    }

    if (id && type) {
      LocalStorageClient.set(DEVICE_CODE_KEY, id);
      LocalStorageClient.set(DEVICE_CODE_BY_KEY, type);
    }
  },
  getSessionId: () => {
    if (window.awsRum) {
      return window.awsRum.eventCache?.sessionManager?.session?.sessionId;
    }
    return null;
  },
  trackEvent: (eventType, attributes = {}) => {
    if (!window.awsRum || !eventType) return;
    window.awsRum.recordEvent(eventType, attributes);
  },
  trackClick: (event) => {
    if (!event?.target) return;

    const trackableElement = event.target.closest('[data-track-click]');
    if (trackableElement && window.awsRum) {
      const buttonId = trackableElement.getAttribute('data-track-click');
      const flushMethod = TrackingService.getFlushMethodForElement(trackableElement);
      TrackingService.trackButtonClick(buttonId, { flushMethod });
    }
  },
  trackButtonClick: (buttonId, { flushMethod = TRACKING_FLUSH_METHODS.NONE } = {}) => {
    if (!buttonId || !window.awsRum) return;

    window.awsRum.recordEvent('click', {
      // TODO other metadata to record?
      buttonId,
    });
    TrackingService.flushEvents(flushMethod);
  },
  flushEvents: (flushMethod = TRACKING_FLUSH_METHODS.NONE) => {
    if (!window.awsRum || flushMethod === TRACKING_FLUSH_METHODS.NONE) return;

    const dispatchFn = window.awsRum[flushMethod];
    if (typeof dispatchFn === 'function') {
      dispatchFn.call(window.awsRum);
    }
  },
  getFlushMethodForElement: (trackableElement) => {
    if (!trackableElement) {
      return TRACKING_FLUSH_METHODS.NONE;
    }

    if (trackableElement.tagName !== 'A') {
      return TRACKING_FLUSH_METHODS.FETCH;
    }

    const href = trackableElement.getAttribute('href');
    if (!href) {
      return TRACKING_FLUSH_METHODS.FETCH;
    }

    return trackableElement.getAttribute('target') === '_blank'
      ? TRACKING_FLUSH_METHODS.FETCH
      : TRACKING_FLUSH_METHODS.BEACON;
  },
  initializeGlobalClickTracking: () => {
    document.removeEventListener('click', TrackingService.trackClick);
    document.addEventListener('click', TrackingService.trackClick);
    // ^ both removeEventListener & addEventListener should reference the same function to work
  },
  // this is how you would use this / extend behavior
  // recordPageView: (pageId) => {
  //   if (window.awsRum) {
  //     window.awsRum.recordPageView({ pageId });
  //   }
  // },
};

export default TrackingService;

// Copyright © 2025 Micruity Inc.
